import {
  DOC_STATUS_LABEL,
  ENTRY_ORIGIN_LABEL,
  INVOICE_MODELS,
  INVOICE_MODEL_LABEL,
  NFE_XML_MAX_BYTES,
  formatMoney,
  purchaseEntryCreateSchema,
  type DocStatus,
  type Page,
  type PurchaseEntry,
} from "@cafegestor/shared";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useCan } from "@/app/me";
import {
  Alert,
  Badge,
  Button,
  Card,
  Field,
  Input,
  PageTitle,
  Select,
  Spinner,
  Table,
} from "@/components/ui";
import { apiFetch, errorDetails, errorMessage } from "@/lib/api";
import { Checkbox, Pager, SaveError, SearchInput } from "../cadastros/widgets";
import { dateBR, today, useDocAction } from "../estoque/lib";
import { DocStatusBadge, WarehouseSelect } from "../estoque/pickers";
import { EntryEditor } from "./EntradaEditor";
import { ENTRY_KEY, usePaymentMethods } from "./lib";
import { SupplierPicker } from "./pickers";

export function EntradasPage() {
  const can = useCan();
  const [panel, setPanel] = useState<"xml" | "manual" | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [status, setStatus] = useState<DocStatus | "todos">("todos");
  const [q, setQ] = useState("");
  const [offset, setOffset] = useState(0);
  const params = new URLSearchParams({ status, limit: "50", offset: String(offset) });
  if (q.trim()) params.set("q", q.trim());
  const list = useQuery({
    queryKey: [...ENTRY_KEY, params.toString()],
    queryFn: () => apiFetch<Page<PurchaseEntry>>(`/compras/entradas?${params.toString()}`),
    placeholderData: (p) => p,
  });
  const opened = (id: string) => {
    setPanel(null);
    setOpen(id);
  };

  return (
    <div className="space-y-6">
      <PageTitle
        title="Entradas de mercadoria"
        description="Notas de compra: importe o XML da NF-e ou lance à mão. Na confirmação, a mercadoria entra no estoque com o custo e as parcelas vão para Contas a pagar. Confirmada, a entrada não volta atrás; correção é por estorno."
        actions={
          <div className="flex gap-2">
            {can("compras.xml.importar") && (
              <Button onClick={() => setPanel("xml")} disabled={panel === "xml"}>
                Importar XML da NF-e
              </Button>
            )}
            {can("compras.entrada.lancar") && (
              <Button
                variant="secondary"
                onClick={() => setPanel("manual")}
                disabled={panel === "manual"}
              >
                Lançar manualmente
              </Button>
            )}
          </div>
        }
      />
      {panel === "xml" && <ImportXml onClose={() => setPanel(null)} onDone={opened} />}
      {panel === "manual" && <NewManualEntry onClose={() => setPanel(null)} onDone={opened} />}
      {open && <EntryEditor key={open} id={open} onClose={() => setOpen(null)} />}
      <Card>
        <div className="mb-4 flex flex-wrap gap-3">
          <SearchInput
            placeholder="Buscar número, fornecedor, nota ou chave"
            value={q}
            onChange={(v) => {
              setQ(v);
              setOffset(0);
            }}
          />
          <Select
            className="w-44"
            value={status}
            onChange={(e) => {
              setStatus(e.target.value as DocStatus | "todos");
              setOffset(0);
            }}
          >
            <option value="todos">Todas</option>
            {(Object.keys(DOC_STATUS_LABEL) as DocStatus[]).map((s) => (
              <option key={s} value={s}>
                {DOC_STATUS_LABEL[s]}
              </option>
            ))}
          </Select>
        </div>
        {list.isPending ? (
          <Spinner />
        ) : (
          <>
            <Table
              head={["Número", "Entrada", "Fornecedor", "Nota", "Origem", "Total", "Situação", ""]}
            >
              {list.data?.items.map((e) => (
                <tr key={e.id}>
                  <td className="px-3 py-2 font-mono">{e.number}</td>
                  <td className="px-3 py-2">{dateBR(e.entryDate)}</td>
                  <td className="px-3 py-2">{e.supplierName}</td>
                  <td className="px-3 py-2">
                    {e.invoiceNumber
                      ? `${e.invoiceNumber}${e.invoiceSeries ? `/${e.invoiceSeries}` : ""}`
                      : "—"}
                  </td>
                  <td className="px-3 py-2">
                    {ENTRY_ORIGIN_LABEL[e.origin]}
                    {e.status === "rascunho" && e.pendingItems > 0 && (
                      <Badge tone="warn">{e.pendingItems} sem vínculo</Badge>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {formatMoney(e.totalAmount)}
                  </td>
                  <td className="px-3 py-2">
                    <DocStatusBadge status={e.status} />
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Button size="sm" variant="ghost" onClick={() => opened(e.id)}>
                      Abrir
                    </Button>
                  </td>
                </tr>
              ))}
            </Table>
            <Pager total={list.data?.total ?? 0} offset={offset} onOffset={setOffset} />
          </>
        )}
      </Card>
    </div>
  );
}

/** Envio do XML: a API confere a nota e cria a entrada em rascunho. */
function ImportXml({ onClose, onDone }: { onClose: () => void; onDone: (id: string) => void }) {
  const [file, setFile] = useState<File | null>(null);
  const [warehouseId, setWarehouseId] = useState("");
  const [localError, setLocalError] = useState<string | null>(null);
  const send = useDocAction<{ xml: string; fileName: string; warehouseId: string }, PurchaseEntry>({
    path: () => "/compras/entradas/importar-xml",
    invalidate: [ENTRY_KEY, ["cadastros", "pessoas"]],
    onDone: (r) => onDone(r.id),
  });
  async function submit() {
    if (!file) return setLocalError("Escolha o arquivo XML da nota.");
    if (file.size > NFE_XML_MAX_BYTES) return setLocalError("O XML tem mais de 5 MB.");
    setLocalError(null);
    let xml: string;
    try {
      xml = await file.text();
    } catch {
      return setLocalError("Não foi possível ler o arquivo. Escolha-o de novo.");
    }
    send.mutate({ xml, fileName: file.name, warehouseId });
  }
  const details = errorDetails(send.error);
  return (
    <Card title="Importar XML da NF-e">
      <div className="grid gap-4 sm:grid-cols-3 sm:items-end">
        <div className="sm:col-span-2">
          <Field
            label="Arquivo XML da NF-e (com o protocolo de autorização, enviado pelo fornecedor)"
            hint="A nota precisa ser destinada ao CNPJ da empresa. São conferidos a chave, o protocolo e o destinatário; a assinatura digital e a situação atual na SEFAZ não são consultadas. Nada entra no estoque até você confirmar."
          >
            <Input
              type="file"
              accept=".xml,text/xml,application/xml"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
          </Field>
        </div>
        <Field label="Depósito de entrada" hint="Vazio: depósito padrão">
          <WarehouseSelect
            value={warehouseId}
            onChange={setWarehouseId}
            allOption="Padrão (fábrica)"
          />
        </Field>
      </div>
      <div className="mt-4 flex gap-2">
        <Button onClick={() => void submit()} disabled={send.isPending || !file}>
          {send.isPending ? "Conferindo..." : "Importar"}
        </Button>
        <Button variant="secondary" onClick={onClose}>
          Cancelar
        </Button>
      </div>
      <div className="mt-3 space-y-2">
        {localError && <Alert tone="error">{localError}</Alert>}
        {send.error !== null && (
          <Alert tone="error">
            {errorMessage(send.error)}
            {details.length > 0 && (
              <ul className="mt-2 list-disc pl-5">
                {details.map((d) => (
                  <li key={d}>{d}</li>
                ))}
              </ul>
            )}
          </Alert>
        )}
      </div>
    </Card>
  );
}

/** Lançamento manual: dados da nota; itens e parcelas em seguida. */
function NewManualEntry({
  onClose,
  onDone,
}: {
  onClose: () => void;
  onDone: (id: string) => void;
}) {
  const methods = usePaymentMethods();
  const [supplierId, setSupplierId] = useState("");
  const [supplierText, setSupplierText] = useState("");
  const [warehouseId, setWarehouseId] = useState("");
  const [entryDate, setEntryDate] = useState(today());
  const [issueDate, setIssueDate] = useState("");
  const [invoiceModel, setInvoiceModel] = useState<string>("55");
  const [invoiceSeries, setInvoiceSeries] = useState("");
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [paymentMethodId, setPaymentMethodId] = useState("");
  const [generatesPayable, setGeneratesPayable] = useState(true);
  const [formError, setFormError] = useState<string | null>(null);
  const create = useDocAction<unknown, PurchaseEntry>({
    path: () => "/compras/entradas",
    invalidate: [ENTRY_KEY],
    onDone: (r) => onDone(r.id),
  });

  function submit() {
    const parsed = purchaseEntryCreateSchema.safeParse({
      supplierId,
      warehouseId,
      entryDate,
      issueDate,
      invoiceModel: invoiceNumber ? invoiceModel : "",
      invoiceSeries,
      invoiceNumber,
      paymentMethodId,
      generatesPayable,
    });
    if (!parsed.success) return setFormError(parsed.error.issues[0]?.message ?? "Dados inválidos");
    setFormError(null);
    create.mutate(parsed.data);
  }

  return (
    <Card title="Nova entrada (lançamento manual)">
      <div className="grid gap-4 sm:grid-cols-4">
        <div className="sm:col-span-2">
          <Field label="Fornecedor">
            <SupplierPicker
              value={supplierId}
              label={supplierText}
              onChange={(id, l) => {
                setSupplierId(id);
                setSupplierText(l);
              }}
            />
          </Field>
        </div>
        <Field label="Depósito de entrada">
          <WarehouseSelect value={warehouseId} onChange={setWarehouseId} />
        </Field>
        <Field label="Data da entrada">
          <Input type="date" value={entryDate} onChange={(e) => setEntryDate(e.target.value)} />
        </Field>
        <Field label="Documento">
          <Select value={invoiceModel} onChange={(e) => setInvoiceModel(e.target.value)}>
            {INVOICE_MODELS.map((m) => (
              <option key={m} value={m}>
                {INVOICE_MODEL_LABEL[m]}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Número da nota" hint="Vazio: entrada sem nota">
          <Input
            inputMode="numeric"
            value={invoiceNumber}
            onChange={(e) => setInvoiceNumber(e.target.value)}
          />
        </Field>
        <Field label="Série">
          <Input
            inputMode="numeric"
            value={invoiceSeries}
            onChange={(e) => setInvoiceSeries(e.target.value)}
          />
        </Field>
        <Field label="Emissão">
          <Input type="date" value={issueDate} onChange={(e) => setIssueDate(e.target.value)} />
        </Field>
        <Field label="Forma de pagamento">
          <Select value={paymentMethodId} onChange={(e) => setPaymentMethodId(e.target.value)}>
            <option value="">—</option>
            {methods.data?.items.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </Select>
        </Field>
        <div className="flex items-end sm:col-span-3">
          <Checkbox
            label="Gera contas a pagar (desmarque em bonificação, remessa ou compra já paga fora do sistema)"
            checked={generatesPayable}
            onChange={(e) => setGeneratesPayable(e.target.checked)}
          />
        </div>
      </div>
      <div className="mt-4 flex gap-2">
        <Button onClick={submit} disabled={create.isPending}>
          Criar rascunho
        </Button>
        <Button variant="secondary" onClick={onClose}>
          Cancelar
        </Button>
      </div>
      <div className="mt-3 space-y-2">
        {formError && <Alert tone="error">{formError}</Alert>}
        <SaveError error={create.error} />
      </div>
    </Card>
  );
}
